import { createFileRoute, type ToOptions } from '@tanstack/react-router'
import {
  Calendar,
  LayoutGrid,
  MapPin,
  Shield,
  SquareCheckBig,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { useEffect } from 'react'
import z from 'zod'

import { typedObjectEntries } from '@gozoki/tools'
import { AppTabs } from '~/components/new/app-ui/app-tabs'
import { RealEstateCalendarTab } from '~/components/routes/development/real-estate/calendar/real-estate-calendar-tab'
import { KanbanFullArea } from '~/components/routes/development/real-estate/kanban/kanban-full-area'
import { RealEstateMapTab } from '~/components/routes/development/real-estate/map/real-estate-map-tab'
import { RealEstateRolesTab } from '~/components/routes/development/real-estate/roles/real-estate-roles-tab'
import { RealEstateTasksTab } from '~/components/routes/development/real-estate/tasks/tasks-tab'
import { useRights } from '~/utils/hooks'

const tabValues = ['map', 'kanban', 'tasks', 'calendar', 'contacts', 'roles'] as const
const REAL_ESTATE_LAST_TAB_STORAGE_KEY = 'gozoki.backOffice.realEstate.lastTab'

export const Route = createFileRoute('/_authenticated/development/real-estate/$tab')({
  component: RealEstatePage,
  params: {
    parse: z.object({ tab: z.enum(tabValues) }).parse,
  },
})

const tabOptions = {
  map: {
    label: 'Carte',
    icon: MapPin,
    toOptions: { to: '/development/real-estate/$tab', params: { tab: 'map' } },
    component: <RealEstateMapTab />,
  },
  kanban: {
    label: 'Kanban',
    icon: LayoutGrid,
    toOptions: { to: '/development/real-estate/$tab', params: { tab: 'kanban' } },
    component: <KanbanFullArea />,
  },
  tasks: {
    label: 'Tâches',
    icon: SquareCheckBig,
    toOptions: { to: '/development/real-estate/$tab', params: { tab: 'tasks' } },
    component: <RealEstateTasksTab />,
  },
  calendar: {
    label: 'Calendrier',
    icon: Calendar,
    toOptions: { to: '/development/real-estate/$tab', params: { tab: 'calendar' } },
    component: <RealEstateCalendarTab />,
  },
  contacts: {
    label: 'Contacts',
    icon: Users,
    toOptions: { to: '/development/real-estate/$tab', params: { tab: 'contacts' } },
    component: <div>Contacts content</div>,
  },
  roles: {
    label: 'Rôles',
    icon: Shield,
    toOptions: { to: '/development/real-estate/$tab', params: { tab: 'roles' } },
    component: <RealEstateRolesTab />,
  },
} as const satisfies Record<
  (typeof tabValues)[number],
  { toOptions: ToOptions; component: React.ReactNode; label: string; icon: LucideIcon }
>

function RealEstatePage() {
  const isSuperAdmin = useRights(['SUPER_ADMIN'])
  const hasReaderRight = useRights(['REAL_ESTATE_READER'])
  const hasAdminRight = useRights(['REAL_ESTATE_ADMIN'])
  const canRead = isSuperAdmin || hasAdminRight || hasReaderRight

  const navigate = Route.useNavigate()
  const { tab } = Route.useParams()

  useEffect(() => {
    try {
      window.localStorage.setItem(REAL_ESTATE_LAST_TAB_STORAGE_KEY, tab)
    } catch {
      // ignore (private mode / blocked storage)
    }
  }, [tab])

  const currentTab = tabOptions[tab]

  if (!canRead) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <div className="text-center">
          <h3 className="text-lg font-semibold mb-2">Accès refusé</h3>
          <p>Vous devez avoir un rôle Foncier & travaux pour accéder à cette page.</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <div style={{ padding: '10px' }}>
        <div style={{ marginBottom: '20px' }}>
          <AppTabs
            tabs={typedObjectEntries(tabOptions).map(([key, value]) => ({
              label: value.label,
              icon: value.icon,
              value: key,
            }))}
            value={tab}
            onTabChange={(v) =>
              navigate({ ...tabOptions[v].toOptions, search: (prev) => ({ ...prev }) })
            }
          />
        </div>
        {currentTab.component}
      </div>
    </>
  )
}
